import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IntegrationHealthBadge } from "../components/shared/IntegrationHealth";
import { FolderSync, Upload, Download, Loader2, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

export default function SharePointPage({ onLogout, userRole, isFeatureEnabled }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchConfig = useCallback(async () => {
    try {
      const [cfgResp, histResp] = await Promise.all([
        axios.get(`${API}/admin/sharepoint-config`, { headers: getAuthHeaders() }),
        axios.get(`${API}/sharepoint/sync-history`, { headers: getAuthHeaders() }),
      ]);
      setConfig(cfgResp.data);
      setHistory(histResp.data || []);
    } catch { toast.error("Failed to load SharePoint config"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/sharepoint-config`, config, { headers: getAuthHeaders() });
      toast.success("SharePoint configuration saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleSync = async (direction) => {
    setSyncing(true);
    try {
      const resp = await axios.post(`${API}/sharepoint/sync?direction=${direction}`, {}, { headers: getAuthHeaders() });
      toast.success(`Sync complete — Pulled: ${resp.data.pulled}, Pushed: ${resp.data.pushed}`);
      fetchConfig();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const update = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));

  if (loading || !config) {
    return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><div className="text-[#64748B] font-mono">Loading SharePoint configuration...</div></div></Layout>;
  }

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="sharepoint-page">
        <PageHeader title="SharePoint Blueprint Sync" subtitle="Pull and push drawings to a SharePoint document library" testId="sharepoint-title">
          <Button onClick={handleSave} disabled={saving} className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="sp-save-btn">
            {saving ? "Saving..." : "Save Configuration"}
          </Button>
        </PageHeader>

        <IntegrationHealthBadge
          integration="sharepoint"
          onTestConnection={async () => {
            const resp = await axios.post(`${API}/admin/sharepoint-test`, {}, { headers: getAuthHeaders() });
            return resp.data;
          }}
        />

        {/* Azure AD Credentials */}
        <div className="bg-white border border-[#E2E8F0] p-6">
          <h3 className="font-bold text-sm text-[#0F172A] mb-4 flex items-center gap-2">
            <FolderSync size={16} /> Azure AD App Registration
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm">Tenant ID *</Label>
              <Input value={config.tenant_id || ""} onChange={(e) => update("tenant_id", e.target.value)} className="mt-1 rounded-none font-mono text-sm" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" data-testid="sp-tenant-id" />
            </div>
            <div>
              <Label className="text-sm">Client ID *</Label>
              <Input value={config.client_id || ""} onChange={(e) => update("client_id", e.target.value)} className="mt-1 rounded-none font-mono text-sm" data-testid="sp-client-id" />
            </div>
            <div>
              <Label className="text-sm">Client Secret *</Label>
              <Input type="password" value={config.client_secret || ""} onChange={(e) => update("client_secret", e.target.value)} className="mt-1 rounded-none font-mono text-sm" data-testid="sp-client-secret" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <Label className="text-sm">SharePoint Site URL *</Label>
              <Input value={config.site_url || ""} onChange={(e) => update("site_url", e.target.value)} className="mt-1 rounded-none font-mono text-sm" placeholder="https://contoso.sharepoint.com/sites/Engineering" data-testid="sp-site-url" />
            </div>
            <div>
              <Label className="text-sm">Document Library Name</Label>
              <Input value={config.library_name || ""} onChange={(e) => update("library_name", e.target.value)} className="mt-1 rounded-none text-sm" placeholder="Blueprints" data-testid="sp-library-name" />
            </div>
          </div>
        </div>

        {/* Sync Controls */}
        <div className="bg-[#0F172A] p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading font-bold text-xl">Manual Sync</h2>
              <p className="text-[#94A3B8] text-sm mt-1">Pull drawings from SharePoint or push local blueprints to the library.</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => handleSync("pull")} disabled={syncing} className="bg-[#10B981] hover:bg-[#059669] text-white rounded-none" data-testid="sp-pull-btn">
                {syncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />}Pull
              </Button>
              <Button onClick={() => handleSync("push")} disabled={syncing} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid="sp-push-btn">
                {syncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}Push
              </Button>
              <Button onClick={() => handleSync("both")} disabled={syncing} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none" data-testid="sp-sync-both-btn">
                {syncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}Full Sync
              </Button>
            </div>
          </div>
        </div>

        {/* Sync History */}
        {history.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-bold text-sm text-[#0F172A] mb-3 flex items-center gap-2"><Clock size={16} /> Sync History</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
                  <tr>
                    {["Date", "Direction", "Pulled", "Pushed", "Errors"].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-bold text-[#0F172A] uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {history.slice(0, 20).map(h => (
                    <tr key={h.id} className="hover:bg-[#FAFAFA]">
                      <td className="px-3 py-2 font-mono text-xs text-[#64748B]">{new Date(h.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2"><span className="px-2 py-0.5 text-xs font-bold bg-[#EFF6FF] text-[#1E40AF] uppercase">{h.direction}</span></td>
                      <td className="px-3 py-2 text-[#10B981] font-bold">{h.pulled}</td>
                      <td className="px-3 py-2 text-[#D97706] font-bold">{h.pushed}</td>
                      <td className="px-3 py-2 text-[#DC2626]">{h.errors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
